import {
  default as BaseCheckbox,
  type CheckboxProps as BaseCheckboxProps,
} from '@mui/joy/Checkbox';
import { CheckboxFieldCustomStyleKeys } from '@unifyapps/defs/blocks/Form/types';
import Check02 from '@unifyapps/icons/outline/Check02';
import Minus from '@unifyapps/icons/outline/Minus';
import { clsx } from 'clsx';
import noop from 'lodash/noop';
import { forwardRef, useCallback, useMemo } from 'react';
import { UA_CHECKBOX } from './const';
import {
  checkboxActionVariants,
  checkboxLabelVariants,
  checkboxRootVariants,
} from './styles';
import type { CheckboxProps } from './types';

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
  const {
    customStyle,
    className,
    size = 'sm',
    onChange,
    onClick,
    slotProps,
    readOnly,
    customVariantDataAttributes,
    ...restProps
  } = props;

  const iconSize = size === 'sm' ? 12 : 14;

  const iconStyle = customStyle?.[CheckboxFieldCustomStyleKeys.Icon] ?? {};

  const resolveLabelSlotProps = useCallback(
    <K extends keyof React.LabelHTMLAttributes<HTMLLabelElement>>(property: K) =>
      slotProps?.label && typeof slotProps.label !== 'function' && property in slotProps.label
        ? (slotProps.label[property] as React.LabelHTMLAttributes<HTMLLabelElement>[K])
        : undefined,
    [slotProps?.label],
  );

  const checkboxSlotProps = useMemo<BaseCheckboxProps['slotProps']>(() => {
    const {
      [CheckboxFieldCustomStyleKeys.Icon]: _iconStyle,
      [CheckboxFieldCustomStyleKeys.Checked]: checkedStyle,
      [CheckboxFieldCustomStyleKeys.Unchecked]: uncheckedStyle,
      ...rootStyle
    } = customStyle ?? {};

    return {
      root: ({ checked, indeterminate, disabled, focusVisible }) => ({
        className: checkboxRootVariants({
          className: clsx(
            className,
            slotProps?.root && 'className' in slotProps.root ? slotProps.root.className : undefined,
          ),
          checked,
          indeterminate,
          disabled,
          readOnly: Boolean(readOnly),
          focusVisible,
          size,
        }),
      }),
      checkbox: ({ checked, indeterminate, disabled }) => {
        // eslint-disable-next-line no-nested-ternary
        const state = indeterminate ? 'indeterminate' : checked ? 'checked' : 'unchecked';

        return {
          className: clsx(
            UA_CHECKBOX,
            'isolate border shrink-0',
            // fixes jumping checkbox, when check icon is mounted
            '[&>svg]:absolute',
            slotProps?.checkbox && 'className' in slotProps.checkbox
              ? slotProps.checkbox.className
              : undefined,
          ),
          style: checked
            ? {
                ...rootStyle,
                ...checkedStyle,
              }
            : {
                ...rootStyle,
                ...uncheckedStyle,
              },
          'data-size': size,
          'data-state': state,
          'data-disabled': disabled ? 'true' : undefined,
          'data-readonly': readOnly ? 'true' : undefined,
          ...customVariantDataAttributes,
        };
      },
      action: ({ checked, indeterminate, disabled, focusVisible }) => ({
        className: checkboxActionVariants({
          checked,
          indeterminate,
          disabled,
          readOnly: Boolean(readOnly),
          focusVisible,
          size,
        }),
        style: {
          textAlign: 'start',
        },
      }),
      label: ({ checked, indeterminate, disabled, focusVisible }) => ({
        tabIndex: resolveLabelSlotProps('tabIndex'),
        onKeyDown: resolveLabelSlotProps('onKeyDown'),
        className: checkboxLabelVariants({
          checked,
          indeterminate,
          disabled,
          readOnly: Boolean(readOnly),
          focusVisible,
          size,
          className:
            slotProps?.label && 'className' in slotProps.label
              ? slotProps.label.className
              : undefined,
        }),
      }),
      input: {
        ref,
        ...(readOnly ? { 'aria-readonly': 'true' } : {}),
      },
    };
  }, [
    ref,
    className,
    slotProps?.root,
    slotProps?.checkbox,
    slotProps?.label,
    size,
    customStyle,
    readOnly,
    resolveLabelSlotProps,
    customVariantDataAttributes,
  ]);

  return (
    <BaseCheckbox
      checkedIcon={<Check02 height={iconSize} strokeWidth={2} style={iconStyle} width={iconSize} />}
      data-size={size}
      disabled={props.disabled}
      indeterminateIcon={
        <Minus height={iconSize} strokeWidth={2} style={iconStyle} width={iconSize} />
      }
      onChange={props.readOnly ? noop : onChange}
      onClick={props.readOnly ? noop : onClick}
      ref={ref}
      size={size}
      slotProps={checkboxSlotProps}
      {...restProps}
      className={className}
      indeterminate={props.indeterminate}
    />
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
